import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function Modal({ 
  onClose, 
  children, 
  title, 
  description, 
  mediaList = [], 
  initialIndex = 0, 
  onNavigate 
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    // Add enter animation class after a brief delay
    const timer = setTimeout(() => {
      const overlay = document.querySelector(".modal-overlay");
      const content = document.querySelector(".modal-content");
      if (overlay) overlay.classList.add("modal-overlay-enter");
      if (content) content.classList.add("modal-content-enter");
    }, 10);

    // Add event listener for Escape key
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    // Ensure video plays on loop if it exists
    if (videoRef.current) {
      videoRef.current.play();
    }

    return () => {
      document.body.style.overflow = "unset";
      clearTimeout(timer);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleClose = () => {
    const overlay = document.querySelector(".modal-overlay");
    const content = document.querySelector(".modal-content");

    // Add exit animation classes
    if (overlay) overlay.classList.add("modal-overlay-exit");
    if (content) content.classList.add("modal-content-exit");

    // Wait for animation to complete before closing
    setTimeout(onClose, 200);
  };

  return createPortal(
    <div 
      className="modal-overlay" 
      onClick={handleClose}
      aria-label="Close modal by clicking outside or pressing ESC"
      role="dialog"
    >
      {mediaList.length > 1 && (
        <div className="modal-navigation">
          <button 
            className="modal-nav modal-nav-prev" 
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(-1);
            }}
            aria-label="Previous image"
          >
            <ArrowLeft size={24} strokeWidth={1.5} />
          </button>
          <button 
            className="modal-nav modal-nav-next" 
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(1);
            }}
            aria-label="Next image"
          >
            <ArrowRight size={24} strokeWidth={1.5} />
          </button>
        </div>
      )}

      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        aria-modal="true"
      >
        {React.cloneElement(children, {
          ref: children.type === 'video' ? videoRef : null
        })}
      </div>
    </div>,
    document.body
  );
}